import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Icon,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  FormControl,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  radioLabelSx,
  selectSx,
} from "components/CommonComponent/CommonFunction";
import { ArrowDropDown } from "@mui/icons-material";
import { STATUS } from "components/common/constant";
import {
  addDutyType,
  getDutyTypeById,
  getDutyTypeList,
  updateDutyTyperById,
  deleteDutyTypeById,
} from "actions/dutyType";
import { useDispatch, useSelector } from "react-redux";
import { dutyTypeList } from "constants/dutyType";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import {
  DEFAULT_RECORDS_PER_PAGE,
  hasAccess,
} from "components/common/constant";

const initialFields = {
  id: null,
  name: {
    value: "",
    original: "",
    error: "",
  },
  totalHours: {
    value: "",
    original: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    original: "ACTIVE",
    error: "",
  },
};

function DutyTypeListing() {
  const dispatch = useDispatch();
  const dutyTypeListData = useSelector((state) => state.dutyType.dutyTypeList);
  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("DUTY_TYPE_MASTER", "CREATE")?.includes(currentRole);
  const canUpdate = hasAccess("DUTY_TYPE_MASTER", "UPDATE")?.includes(currentRole);
  const canDelete = hasAccess("DUTY_TYPE_MASTER", "DELETE")?.includes(currentRole);

  const canShowActions = canUpdate || canDelete;

  const [panelMode, setPanelMode] = useState(null);
  const [formData, setFormData] = useState(initialFields);
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filters, setFilters] = useState({
    name: "",
    code: "",
    status: "",
    filterParams: [],
  });
  const { notify } = useNotify();

  useEffect(() => {
    getDutyTypeList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({ type: dutyTypeList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (dutyTypeListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: dutyTypeListData?.data?.DutyType?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (dutyTypeListData?.code || dutyTypeListData?.message) {
      setListState({
        isLoading: false,
        error:
          dutyTypeListData?.message ||
          "Failed to load duty type list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [dutyTypeListData]);

  const tableData =
    dutyTypeListData?.data?.DutyType?.map((item, index) => ({
      ...item,
      id: item?.ID,
      srNo:
        (pageNum - 1) *
          (dutyTypeListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      name: item?.name || "-",
      totalHours:
        item?.totalHours != null && Number(item.totalHours) !== 0
          ? Number(item.totalHours).toFixed(2)
          : "-",
      status: item?.status || "-",
    })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.name?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name.trim()],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmed = value?.trim();

    switch (name) {
      case "name":
        return !trimmed ? "Please enter duty type name" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const error = getValidationError(name, value);
    setFormData((prev) => ({
      ...prev,
      [name]: { value: value.trim(), error },
    }));
  };

  const validateForm = () => {
    let newData = { ...formData };
    Object.keys(newData).forEach((key) => {
      if (key === "id") return;
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setFormData(newData);
    return Object.keys(newData)
      .filter((key) => key !== "id")
      .every((key) => newData[key].error === "");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      let payload = {};

      Object.keys(formData).forEach((key) => {
        if (key === "id") return;

        const field = formData[key];

        if (panelMode !== "edit" || field.value !== field.original) {
          if (field.value !== "" || field.original !== "") {
            if (key === "totalHours") {
              const numericValue = Number(field.value);
              payload[key] = Number.isNaN(numericValue)
                ? field.value
                : numericValue;
            } else {
              payload[key] = field.value;
            }
          }
        }
      });

      if (panelMode === "edit" && !Object.keys(payload).length) {
        notify("No changes detected", "info");
        setListState((prev) => ({
          ...prev,
          showLoaderOnClick: false,
        }));
        return;
      }

      const res =
        panelMode === "edit"
          ? await updateDutyTyperById(formData.id, payload)
          : await addDutyType(payload);

      if (res?.code === 200) {
        notify(
          res?.message ||
            (panelMode === "edit"
              ? "Duty type updated successfully"
              : "Duty type added successfully"),
          "success",
        );

        closeModal();
        getDutyTypeList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save duty type";

        notify(message, "error");
      }
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setFormData(initialFields);
  };

  const openAddModal = () => {
    setPanelMode("add");
    setFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const res = await getDutyTypeById(row.id);

    if (res?.code === 200) {
      const dept = res?.data;

      setFormData({
        id: dept?.ID,
        name: {
          value: dept?.name || "",
          original: dept?.name || "",
          error: "",
        },
        totalHours: {
          value: dept?.totalHours || "",
          original: dept?.totalHours || "",
          error: "",
        },
        status: {
          value: dept?.status?.toUpperCase() || "ACTIVE",
          original: dept?.status?.toUpperCase() || "ACTIVE",
          error: "",
        },
      });
    } else {
      const message = Array.isArray(res?.message)
        ? res?.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
        : res?.message || "Failed to fetch duty type details";
      notify(message, "error");
    }

    setListState((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  const handleDelete = (row) => {
    showAlert({
      title: "Are you sure you want to delete this duty type?",
      message: `Duty type "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));

          const res = await deleteDutyTypeById(row.id);

          if (res?.code === 200) {
            notify(res?.message || "Duty type deleted successfully", "success");

            // refresh list
            getDutyTypeList(dispatch, pageNum, filters.filterParams);
          } else {
            notify(res?.message || "Failed to delete duty type", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },

      onCancel: () => {},
    });
  };

  const handleClear = () => {
    setFilters({
      name: "",
      code: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilters((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const renderStatusBadge = (status) => (
    <MDBadge
      badgeContent={status}
      color={status === "ACTIVE" ? "success" : "secondary"}
      variant="gradient"
      size="sm"
      circular
    />
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    { Header: "Sr No", accessor: "srNo", align: "left" },
    { Header: "Duty Type Name", accessor: "name", align: "left" },
    { Header: "Total Hours", accessor: "totalHours", align: "center" },

    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption">|</MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* HEADER */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Duty Types
                </MDTypography>
                {canCreate && (
                  <MDButton
                    variant="contained"
                    color="deepBlue"
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      textTransform: "none",
                    }}
                    onClick={openAddModal}
                  >
                    Add Duty Type
                  </MDButton>
                )}
              </MDBox>

              {/* FILTERS */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by name"
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        IconComponent={ArrowDropDown}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                        renderValue={(selected) =>
                          !selected ? (
                            <span style={{ color: "rgba(0,0,0,0.38)" }}>
                              Select Status
                            </span>
                          ) : (
                            renderStatusBadge(selected)
                          )
                        }
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* TOTAL RECORDS */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {dutyTypeListData?.data?.totalRecords}
                </MDTypography>
              </MDBox>

              {/* TABLE */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No duty types found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (dutyTypeListData?.data?.totalRecords || 0) /
                        (dutyTypeListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* DRAWER */}
        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              {panelMode === "edit" ? "Edit Duty Type" : "Add Duty Type"}
            </MDTypography>

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Name</RequiredLabel>

                  <MDInput
                    variant="outlined"
                    type="text"
                    name="name"
                    placeholder="Enter duty type name"
                    value={formData.name.value}
                    onChange={(e) => handleChange("name", e.target.value)}
                    onBlur={(e) => handleOnBlur("name", e.target.value)}
                    error={!!formData.name.error}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />

                  {formData.name.error && (
                    <MDTypography variant="caption" color="error">
                      {formData.name.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Total Hours</RequiredLabel>

                  <MDInput
                    variant="outlined"
                    type="text"
                    name="totalHours"
                    placeholder="Enter total hours"
                    value={formData.totalHours.value}
                    onChange={(e) => handleChange("totalHours", e.target.value)}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    value={formData.status.value}
                    onChange={(e) => handleChange("status", e.target.value)}
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default DutyTypeListing;
